import { Check, Globe, Users, Lock, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHasFeature } from "@/hooks/use-plan";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type CommentVisibility = "public" | "internal";

const OPTIONS: {
  key: CommentVisibility;
  label: string;
  description: string;
  Icon: typeof Globe;
}[] = [
  {
    key: "public",
    label: "Public Comment",
    description: "All members and Share viewers can see",
    Icon: Globe,
  },
  {
    key: "internal",
    label: "Internal Comment",
    description: "Only members with access can see",
    Icon: Users,
  },
];

type Props = {
  value: CommentVisibility;
  onChange: (next: CommentVisibility) => void;
  /** Compact = icon-only trigger; expanded = icon + label pill. */
  compact?: boolean;
  /** Viewer context. Only workspace members can be routed to the Plan page to
   *  upgrade; guests on a share token have no plan to buy, so their locked
   *  Internal option stays a plain no-op. */
  mode?: "workspace" | "guest" | "share";
};

/**
 * Public / Internal (team-only) visibility picker (2026-07-11).
 *
 * Public = default; visible to everyone with access to the video (workspace
 * members + all guests on a share link).
 * Internal = workspace-only; guests reading via a share token don't see the
 * comment. Server-side filter enforces this in GET /watch/:token/comments.
 *
 * Internal is plan-gated to Studio+ for members (useHasFeature); the option is
 * locked below that tier and the server 403s a member's internal POST. Guests
 * always post public (server-forced), so their picker state is moot.
 *
 * When a workspace member on a below-Studio plan clicks the locked Internal
 * option, we route them to the Plan page to upgrade — matching the soft-lock
 * behavior of every other gated control (see billing/feature-lock.tsx). Before
 * this, the locked row was a dead disabled button with no upgrade path.
 */
export function VisibilityDropdown({ value, onChange, compact, mode = "workspace" }: Props) {
  const current = OPTIONS.find((o) => o.key === value) ?? OPTIONS[0];
  const CurrentIcon = current.Icon;
  const canInternal = useHasFeature("internalComments");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Only workspace members can upgrade — guests/share viewers have no plan page.
  const canUpgrade = mode === "workspace";
  const planHref = pathname.startsWith("/admin")
    ? "/admin/settings/plan"
    : "/app/settings/plan";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={current.label}
        title={current.label}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-border bg-background text-xs font-medium text-foreground transition-colors hover:bg-secondary/40",
          compact ? "h-7 w-7 justify-center" : "h-7 px-2",
        )}
      >
        <CurrentIcon className="h-3.5 w-3.5" />
        {!compact && (
          <>
            <span>{value === "internal" ? "Internal" : "Public"}</span>
            <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0 text-muted-foreground">
              <path d="M3 5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-1">
        {OPTIONS.map(({ key, label, description, Icon }) => {
          const active = value === key;
          const locked = key === "internal" && !canInternal;
          // A locked row is either an upgrade CTA (member) or an inert lock (guest).
          const upgradeable = locked && canUpgrade;
          const title = locked
            ? upgradeable
              ? "Internal comments are available on the Studio plan and above — click to upgrade"
              : "Internal comments are available on the Studio plan and above"
            : label;
          return (
            <button
              key={key}
              type="button"
              disabled={locked && !upgradeable}
              onClick={() => {
                if (!locked) return onChange(key);
                if (upgradeable) navigate(planHref);
              }}
              title={title}
              className={cn(
                "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors",
                locked
                  ? upgradeable
                    ? "cursor-pointer opacity-60 hover:bg-secondary/40 hover:opacity-100"
                    : "cursor-not-allowed opacity-60"
                  : active
                    ? "bg-secondary/60"
                    : "hover:bg-secondary/40",
              )}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground">{label}</span>
                  {locked && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-secondary px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Lock className="h-2.5 w-2.5" /> Studio
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground">{description}</div>
                {upgradeable && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary">
                    <Sparkles className="h-3 w-3" strokeWidth={2} /> Upgrade to unlock
                  </div>
                )}
              </div>
              {active && !locked && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
